import { useEffect, useRef, useState, useCallback } from 'react';

export enum SaveStatus {
  IDLE = 'idle',
  SAVING = 'saving',
  SAVED = 'saved',
  ERROR = 'error',
}

type UseAutoSaveOptions<T> = {
  data: T;
  saveFn: (data: T) => Promise<void>;
  delay?: number;
  retries?: number;
  onStatusChange?: (status: SaveStatus) => void;
  isEqual?: (a: T, b: T) => boolean; // optional deep comparison
  disabled?: boolean;
};

const useAutoSave = <T>({
  data,
  saveFn,
  delay = 2000,
  retries = 3,
  onStatusChange,
  isEqual = (a, b) => JSON.stringify(a) === JSON.stringify(b), // default deep equality check
  disabled = false,
}: UseAutoSaveOptions<T>): SaveStatus => {
  const [status, setStatus] = useState<SaveStatus>(SaveStatus.IDLE);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const retryCountRef = useRef(0);
  const lastSavedDataRef = useRef<T>(data);

  const updateStatus = useCallback(
    (newStatus: SaveStatus) => {
      setStatus(newStatus);
      onStatusChange?.(newStatus);
    },
    [onStatusChange]
  );

  const clearTimeoutRef = (): void => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  const saveWithRetry = useCallback(
    async (payload: T, attempt = 1, backoff = 1000) => {
      if (disabled) return;
      updateStatus(SaveStatus.SAVING);
      try {
        await saveFn(payload);
        lastSavedDataRef.current = payload; // update last saved reference
        updateStatus(SaveStatus.SAVED);
        retryCountRef.current = 0;
        setTimeout(() => updateStatus(SaveStatus.IDLE), 1500);
      } catch (err) {
        if (attempt <= retries) {
          setTimeout(() => saveWithRetry(payload, attempt + 1, backoff * 2), backoff);
        } else {
          updateStatus(SaveStatus.ERROR);
        }
      }
    },
    [saveFn, retries, updateStatus, disabled]
  );

  useEffect(() => {
    if (isEqual(data, lastSavedDataRef.current)) return;

    clearTimeoutRef();
    timeoutRef.current = setTimeout(() => {
      saveWithRetry(data);
    }, delay);

    return () => {
      clearTimeoutRef();
    };
  }, [data, delay, isEqual, saveWithRetry]);

  return status;
};

export default useAutoSave;
